import React from 'react'
import { AlertTriangle, Clock, CheckCircle, Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useAlerts } from '@/contexts/AlertContext'
import { cn } from '@/lib/utils'

interface AlertDashboardWidgetProps {
  compact?: boolean
  showHistory?: boolean
  maxAlerts?: number
  className?: string
}

export const AlertDashboardWidget: React.FC<AlertDashboardWidgetProps> = ({
  compact = false,
  showHistory = false,
  maxAlerts = 5,
  className
}) => {
  const { activeAlerts, alertHistory, acknowledgeAlert, dismissAlert } = useAlerts()

  const alertCounts = {
    critical: activeAlerts.filter(a => a.severity === 'critical').length,
    high: activeAlerts.filter(a => a.severity === 'high').length,
    medium: activeAlerts.filter(a => a.severity === 'medium').length,
    low: activeAlerts.filter(a => a.severity === 'low').length
  }

  const displayAlerts = showHistory 
    ? alertHistory.slice(0, maxAlerts)
    : activeAlerts.slice(0, maxAlerts)

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-risk-critical text-white'
      case 'high': return 'bg-risk-high text-white'
      case 'medium': return 'bg-risk-medium text-white'
      case 'low': return 'bg-risk-low text-white'
      default: return 'bg-muted text-muted-foreground'
    }
  }

  const getAlertIcon = (type: string) => {
    const icons = {
      sequential: '📱',
      velocity: '⚡',
      datacenter: '🖥️',
      domain: '📧',
      zip: '📍',
      pattern: '🔍'
    }
    return icons[type as keyof typeof icons] || '🚨'
  }

  const formatTime = (date: Date) => {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    
    if (minutes < 1) return 'now'
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h`
    return `${Math.floor(hours / 24)}d`
  }

  if (compact) {
    return (
      <div className={cn("bg-card border rounded-lg p-4", className)}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm text-foreground">Active Alerts</h3>
          <div className="flex items-center gap-1">
            {alertCounts.critical > 0 && (
              <Badge className={getSeverityColor('critical')} variant="secondary">
                {alertCounts.critical}
              </Badge>
            )}
            {alertCounts.high > 0 && (
              <Badge className={getSeverityColor('high')} variant="secondary">
                {alertCounts.high}
              </Badge>
            )}
          </div>
        </div>
        
        <div className="space-y-2">
          {displayAlerts.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground text-sm">
              <CheckCircle className="h-8 w-8 mx-auto mb-2 text-status-approved" />
              No active alerts
            </div>
          ) : (
            displayAlerts.map((alert) => (
              <div
                key={alert.id}
                className={cn(
                  "flex items-center justify-between p-2 rounded border-l-4 transition-all duration-200",
                  alert.severity === 'critical' && "border-l-risk-critical bg-risk-critical/5",
                  alert.severity === 'high' && "border-l-risk-high bg-risk-high/5",
                  alert.severity === 'medium' && "border-l-risk-medium bg-risk-medium/5",
                  alert.severity === 'low' && "border-l-risk-low bg-risk-low/5",
                  alert.acknowledged && "opacity-50"
                )}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-sm">{getAlertIcon(alert.type)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate text-foreground">
                      {alert.title}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatTime(alert.timestamp)} • {alert.affectedLeads} leads
                    </p>
                  </div>
                </div>
                
                {!alert.acknowledged && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => acknowledgeAlert(alert.id)}
                    className="h-6 w-6 p-0 hover:bg-muted"
                  >
                    <CheckCircle className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={cn("bg-card border rounded-lg p-6", className)}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-foreground" />
          <h3 className="font-semibold text-lg text-foreground">Alert Center</h3>
        </div>
        <div className="flex items-center gap-2">
          {Object.entries(alertCounts).map(([severity, count]) => (
            count > 0 && (
              <Badge
                key={severity}
                className={getSeverityColor(severity)}
                variant="secondary"
              >
                <span className="capitalize">{severity}</span>
                <span className="ml-1">{count}</span>
              </Badge>
            )
          ))}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="text-center">
          <div className="text-2xl font-bold text-risk-critical">
            {alertCounts.critical}
          </div>
          <div className="text-xs text-muted-foreground">Critical</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-risk-high">
            {alertCounts.high}
          </div>
          <div className="text-xs text-muted-foreground">High</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-risk-medium">
            {alertCounts.medium}
          </div>
          <div className="text-xs text-muted-foreground">Medium</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-foreground">
            {activeAlerts.length}
          </div>
          <div className="text-xs text-muted-foreground">Total Active</div>
        </div>
      </div>

      <div className="space-y-3">
        {displayAlerts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle className="h-12 w-12 mx-auto mb-3 text-status-approved" />
            <p className="font-medium">All clear!</p>
            <p className="text-sm">No active alerts to display</p>
          </div>
        ) : (
          displayAlerts.map((alert) => (
            <div
              key={alert.id}
              className={cn(
                "flex items-start gap-4 p-4 rounded-lg border transition-all duration-200",
                alert.acknowledged ? "bg-muted/50 opacity-75" : "bg-background",
                alert.severity === 'critical' && !alert.acknowledged && "border-risk-critical bg-risk-critical/5",
                alert.severity === 'high' && !alert.acknowledged && "border-risk-high bg-risk-high/5"
              )}
            >
              <div className="text-2xl flex-shrink-0">
                {getAlertIcon(alert.type)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h4 className="font-medium text-foreground">
                    {alert.title}
                  </h4>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge className={getSeverityColor(alert.severity)} variant="secondary">
                      {alert.severity.toUpperCase()}
                    </Badge>
                    {alert.acknowledged && (
                      <Badge variant="outline" className="text-xs">
                        Acknowledged
                      </Badge>
                    )}
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground mb-3">
                  {alert.message}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>{formatTime(alert.timestamp)} ago</span>
                    </div>
                    <div>
                      {alert.affectedLeads} leads affected
                    </div>
                    {alert.acknowledgedBy && (
                      <div>
                        Acked by {alert.acknowledgedBy}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {alert.actions?.map((action) => (
                      <Button
                        key={action.id}
                        size="sm"
                        variant={action.type === 'destructive' ? 'destructive' : 'outline'}
                        onClick={action.handler}
                        className="h-7 text-xs"
                      >
                        {action.label}
                      </Button>
                    ))}
                    
                    {!alert.acknowledged && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => acknowledgeAlert(alert.id)}
                        className="h-7 text-xs"
                      >
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Acknowledge
                      </Button>
                    )}
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => dismissAlert(alert.id)}
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                    >
                      <Eye className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      {displayAlerts.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <Button variant="outline" className="w-full" size="sm">
            View All Alerts History
          </Button>
        </div>
      )}
    </div>
  )
}